// src/pages/EquipmentDirectory.js

import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import UnifiedDirectory from "../components/UnifiedDirectory";
import {
  getEquipment,
  createEquipment,
  updateEquipment,
  deleteEquipment,
  getEquipmentGroups,
  getGroupsForEquipment,
  updateEquipmentGroups,
} from "../api/enterprise";
import { notifyError } from "../utils/toasts";

function EquipmentDirectory({ divisionId }) {
  const { t } = useTranslation();
  const [equipment, setEquipment] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  useEffect(() => {
    const fetchEquipment = async () => {
      if (divisionId) {
        try {
          const result = await getEquipment(divisionId);
          setEquipment(result);
        } catch (error) {
          notifyError(error?.response?.data?.message || error.message);
        }
      }
    };

    fetchEquipment();
  }, [divisionId]);

  const fetchGroupList = useCallback(async () => {
    if (divisionId) {
      try {
        const groups = await getEquipmentGroups(divisionId);
        const formattedGroups = groups.map((group) => ({
          id: group.equipment_group_id,
          name: group.name,
        }));
        setGroupList(formattedGroups);
      } catch (error) {
        notifyError(error?.response?.data?.message || error.message);
      }
    }
  }, [divisionId]);

  const fetchItemAssociations = useCallback(async (equipmentId) => {
    try {
      const equipmentData = await getGroupsForEquipment(equipmentId);
      const groups = equipmentData.equipment_groups || [];
      return groups.map((group) => ({
        id: group.group_id,
        name: group.group_name,
      }));
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  }, []);

  const updateItemAssociations = async (equipmentId, groupIds) => {
    try {
      await updateEquipmentGroups(equipmentId, groupIds);
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
      return null;
    }
  };

  const handleAddEquipment = async (name, status, description, selectedGroupIds) => {
    try {
      const newEquipment = await createEquipment(
        divisionId,
        name,
        status,
        description,
        selectedGroupIds
      );
      const result = await getEquipment(divisionId); // Обновляем список после добавления
      setEquipment(result);
      return newEquipment; // Возвращаем новое оборудование
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
      return null;
    }
  };

  const handleEditEquipment = async (
    item,
    newName,
    newStatus,
    newDescription
  ) => {
    try {
      await updateEquipment(
        item.equipment_id,
        newName,
        newStatus,
        newDescription
      );
      const result = await getEquipment(divisionId); // Обновляем список после редактирования
      setEquipment(result);
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  };

  const handleDeleteEquipment = async (item) => {
    try {
      await deleteEquipment(item.equipment_id);
      const result = await getEquipment(divisionId); // Обновляем список после удаления
      setEquipment(result);
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  };

  return (
    <UnifiedDirectory
      title={t("equipment")}
      data={equipment}
      onAdd={handleAddEquipment}
      onEdit={handleEditEquipment}
      onDelete={handleDeleteEquipment}
      fields={["name", "status", "description"]}
      displayFields={["name", "description", "status", "upserted_at"]}
      divisionId={divisionId}
      associatedList={groupList}
      fetchAssociatedList={fetchGroupList}
      fetchItemAssociations={fetchItemAssociations}
      updateItemAssociations={updateItemAssociations}
      associatedIdsState={selectedGroupIds}
      associatedIdsSetter={setSelectedGroupIds}
      associatedLabel="select_groups"
      itemIdKey="equipment_id"
    />
  );
}

export default EquipmentDirectory;
